<div class="page-container">
  <div class="container">
    <mat-card class="settings-card">
      <div class="settings-card-header">
        <button
          mat-icon-button
          type="button"
          class="settings-back-btn"
          routerLink="/profile"
          aria-label="Back to profile">
          <mat-icon>arrow_back</mat-icon>
        </button>
        <div class="settings-card-header-text">
          <div class="settings-card-title-row">
            <mat-icon class="settings-title-icon">settings</mat-icon>
            <span class="settings-card-title">Settings</span>
          </div>
          <p class="settings-card-subtitle">
            {{
              isStaffRole
                ? (isAdminRole
                    ? "Notification alerts and signup purpose categories"
                    : "Choose which events send in-app and email alerts")
                : "Manage your account preferences"
            }}
          </p>
        </div>
        <div class="settings-card-header-spacer" aria-hidden="true"></div>
      </div>

      <mat-card-content class="settings-card-body">
        <div *ngIf="loading" class="loading-section">
          <mat-spinner diameter="40" class="settings-loading-spinner"></mat-spinner>
          <p>Loading settings...</p>
        </div>

        <form [formGroup]="settingsForm" (ngSubmit)="onSubmit()" *ngIf="!loading && canViewTextNotifications">
          <section class="settings-panel">
            <div class="settings-panel__head">
              <mat-icon>notifications</mat-icon>
              <div>
                <h3>Notification Preferences</h3>
                <p>Receive SMS notifications for important updates and alerts</p>
              </div>
            </div>

            <div class="setting-item">
              <div class="setting-info">
                <h4>Text Notifications</h4>
                <p>Receive SMS notifications for important updates and alerts</p>
                <p class="text-notification-verification-note">
                  To enable or receive text notifications, you must complete mobile verification when you turn this on.
                </p>
              </div>
              <mat-slide-toggle formControlName="textnotifications" color="primary" [disabled]="saving">
              </mat-slide-toggle>
            </div>
          </section>

          <div class="form-actions">
            <button type="button" mat-stroked-button (click)="onCancel()" [disabled]="saving">
              <mat-icon>cancel</mat-icon>
              Cancel
            </button>

            <button
              type="submit"
              mat-raised-button
              color="primary"
              [disabled]="settingsForm.invalid || saving || settingsForm.pristine">
              <mat-icon *ngIf="!saving">save</mat-icon>
              <mat-icon *ngIf="saving" class="spinning">refresh</mat-icon>
              {{ saving ? "Saving..." : "Save Settings" }}
            </button>
          </div>
        </form>

        <div class="settings-stack" *ngIf="!loading && isStaffRole">
          <section class="settings-panel">
            <div class="settings-panel__head">
              <mat-icon>notifications</mat-icon>
              <div>
                <h3>Notification Preferences</h3>
                <p>Control which events you are notified about and how you receive them.</p>
              </div>
            </div>

            <div *ngIf="loadingPreferences" class="pref-loading">
              <span>Loading preferences...</span>
            </div>

            <ng-container *ngIf="!loadingPreferences">
              <div class="pref-grid-header">
                <span class="pref-col-event">Event</span>
                <span class="pref-col-toggle">In-App</span>
                <span class="pref-col-toggle">Email</span>
              </div>

              <div class="pref-grid-row"
                *ngFor="let pref of visibleNotificationPreferences; trackBy: trackByEventType">

                <div class="pref-col-event">
                  <span class="pref-event-label">
                    {{ pref.label }}
                  </span>
                  <span class="pref-event-desc">
                    {{ pref.description }}
                  </span>
                </div>

                <div class="pref-col-toggle">
                  <label class="rad-toggle" *ngIf="pref.supports_inapp !== false">
                    <input type="checkbox"
                      [checked]="pref.inapp_enabled === 1"
                      [disabled]="savingPageChanges"
                      (change)="toggleInApp(pref)">
                    <span class="rad-toggle-slider"></span>
                  </label>
                  <span class="pref-event-desc" *ngIf="pref.supports_inapp === false">—</span>
                </div>

                <div class="pref-col-toggle">
                  <label class="rad-toggle">
                    <input type="checkbox"
                      [checked]="pref.email_enabled === 1"
                      [disabled]="savingPageChanges"
                      (change)="toggleEmail(pref)">
                    <span class="rad-toggle-slider"></span>
                  </label>
                </div>
              </div>
            </ng-container>
          </section>

          <section class="settings-panel" *ngIf="isAdminRole">
            <div class="settings-panel__head">
              <mat-icon>category</mat-icon>
              <div>
                <h3>Signup Purpose Categories</h3>
                <p>Categories shown on the individual signup form. Inactive categories stay in history but are hidden from new signups.</p>
              </div>
            </div>

            <div *ngIf="loadingSignupCategories" class="pref-loading">
              <span>Loading categories...</span>
            </div>

            <ng-container *ngIf="!loadingSignupCategories">
              <div class="pref-grid-header category-grid-header">
                <span class="pref-col-event">Category</span>
                <span class="pref-col-toggle">Actions</span>
                <span class="pref-col-toggle">Active</span>
              </div>

              <div
                class="pref-grid-row category-grid-row"
                *ngFor="let category of signupPurposeCategories; trackBy: trackByCategoryId">
                <div class="pref-col-event">
                  <ng-container *ngIf="editingCategoryId !== category.id">
                    <span class="pref-event-label">{{ category.label }}</span>
                    <span class="pref-event-desc">{{ category.is_active ? 'Shown on signup' : 'Hidden from signup' }}</span>
                  </ng-container>
                  <input
                    *ngIf="editingCategoryId === category.id"
                    type="text"
                    class="category-edit-input"
                    [(ngModel)]="editingCategoryLabel"
                    [ngModelOptions]="{ standalone: true }"
                    maxlength="80"
                    (keyup.enter)="saveEditSignupCategory(category)"
                    (keyup.escape)="cancelEditSignupCategory()" />
                </div>

                <div class="pref-col-toggle category-edit-actions">
                  <ng-container *ngIf="editingCategoryId !== category.id">
                    <button
                      type="button"
                      mat-icon-button
                      class="category-icon-btn"
                      aria-label="Edit category"
                      (click)="startEditSignupCategory(category)">
                      <mat-icon>edit</mat-icon>
                    </button>
                    <button
                      type="button"
                      mat-icon-button
                      class="category-icon-btn category-icon-btn--danger"
                      aria-label="Delete category"
                      [disabled]="savingSignupCategory || savingPageChanges"
                      (click)="deleteSignupPurposeCategory(category)">
                      <mat-icon>delete</mat-icon>
                    </button>
                  </ng-container>
                  <ng-container *ngIf="editingCategoryId === category.id">
                    <button
                      type="button"
                      mat-icon-button
                      class="category-icon-btn"
                      aria-label="Save category"
                      [disabled]="savingSignupCategory"
                      (click)="saveEditSignupCategory(category)">
                      <mat-icon>check</mat-icon>
                    </button>
                    <button
                      type="button"
                      mat-icon-button
                      class="category-icon-btn"
                      aria-label="Cancel edit"
                      (click)="cancelEditSignupCategory()">
                      <mat-icon>close</mat-icon>
                    </button>
                  </ng-container>
                </div>

                <div class="pref-col-toggle">
                  <label class="rad-toggle">
                    <input
                      type="checkbox"
                      [checked]="category.is_active"
                      [disabled]="savingPageChanges"
                      (change)="toggleSignupCategoryActive(category)">
                    <span class="rad-toggle-slider"></span>
                  </label>
                </div>
              </div>

              <div class="category-add-row">
                <input
                  type="text"
                  class="category-add-input"
                  placeholder="New category label"
                  [(ngModel)]="newCategoryLabel"
                  [ngModelOptions]="{ standalone: true }"
                  maxlength="80"
                  (keyup.enter)="addSignupPurposeCategory()" />
                <button
                  type="button"
                  class="pref-save-btn"
                  [disabled]="savingSignupCategory || !newCategoryLabel.trim()"
                  (click)="addSignupPurposeCategory()">
                  <mat-icon>add</mat-icon>
                  <span>Add category</span>
                </button>
              </div>
            </ng-container>
          </section>
        </div>

        <!-- Station order lead time (enforced by preview/create/maximums/payment on the backend). -->
        <div class="settings-stack order-rule-stack" *ngIf="!loading && canAccessOrderRulesSection">
          <section class="settings-panel order-rule-panel" data-testid="order-rule-section">
            <div class="settings-panel__head">
              <mat-icon>event_available</mat-icon>
              <div>
                <h3>Order Lead Time</h3>
                <p>How far ahead advertisers must book. Campaigns cannot start before the earliest start date for the station.</p>
              </div>
            </div>

            <div *ngIf="loadingStationsForRules" class="pref-loading">
              <span>Loading stations...</span>
            </div>

            <form
              *ngIf="!loadingStationsForRules"
              [formGroup]="orderRuleForm"
              class="order-rule-form"
              (ngSubmit)="saveOrderRule()">
              <p *ngIf="manageableStationsForRules.length === 0" class="order-rule-empty">
                You do not manage any stations.
              </p>

              <ng-container *ngIf="manageableStationsForRules.length > 0">
                <div class="order-rule-fields">
                  <mat-form-field
                    *ngIf="showStationDropdown"
                    appearance="outline"
                    class="order-rule-field order-rule-station"
                    subscriptSizing="dynamic">
                    <mat-label>Station</mat-label>
                    <mat-select
                      formControlName="stationId"
                      data-testid="order-rule-station"
                      (selectionChange)="onOrderRuleStationSelectionChange()">
                      <mat-option *ngFor="let s of manageableStationsForRules" [value]="s.id">
                        {{ s.name }} ({{ s.call_sign }})
                      </mat-option>
                    </mat-select>
                  </mat-form-field>

                  <div *ngIf="!showStationDropdown" class="order-rule-station-static">
                    <span class="order-rule-static-label">Station</span>
                    <span class="order-rule-static-value">{{ orderRuleStationLabel }}</span>
                  </div>

                  <mat-form-field appearance="outline" class="order-rule-field" subscriptSizing="dynamic">
                    <mat-label>Lead time type</mat-label>
                    <mat-select formControlName="leadTimeType" data-testid="order-rule-type">
                      <mat-option *ngFor="let t of leadTimeTypes" [value]="t.value">{{ t.label }}</mat-option>
                    </mat-select>
                  </mat-form-field>

                  <mat-form-field appearance="outline" class="order-rule-field order-rule-value" subscriptSizing="dynamic">
                    <mat-label>Lead time ({{ leadTimeUnitLabel }})</mat-label>
                    <input
                      matInput
                      type="number"
                      inputmode="numeric"
                      min="0"
                      [max]="maxLeadTimeValue"
                      step="1"
                      formControlName="leadTimeValue"
                      data-testid="order-rule-value" />
                    <mat-error *ngIf="orderRuleForm.get('leadTimeValue')?.invalid">
                      Enter a whole number from 0 to {{ maxLeadTimeValue }}.
                    </mat-error>
                  </mat-form-field>
                </div>

                <div *ngIf="loadingOrderRule" class="pref-loading">
                  <span>Loading current rule...</span>
                </div>

                <div *ngIf="!loadingOrderRule" class="order-rule-preview" data-testid="order-rule-preview">
                  <div *ngIf="authoritativeMinimum; else previewOnly">
                    <span class="order-rule-static-label">Earliest start date</span>
                    <span class="order-rule-static-value">{{ authoritativeMinimumDisplay() }}</span>
                  </div>
                  <ng-template #previewOnly>
                    <span class="order-rule-static-label">Earliest start date (preview)</span>
                    <span class="order-rule-static-value">{{ orderRulePreviewLabel }}</span>
                    <span class="order-rule-hint">
                      Estimated from your device date. The station's business-timezone date is confirmed when you save.
                    </span>
                  </ng-template>
                </div>

                <div class="order-rule-actions">
                  <button
                    type="submit"
                    class="pref-save-btn"
                    data-testid="order-rule-save"
                    [disabled]="!canSaveOrderRule">
                    <mat-icon *ngIf="!savingOrderRule">save</mat-icon>
                    <mat-icon *ngIf="savingOrderRule" class="spinning">refresh</mat-icon>
                    <span>{{ savingOrderRule ? "Saving..." : "Save lead time" }}</span>
                  </button>
                </div>
              </ng-container>
            </form>
          </section>
        </div>
      </mat-card-content>

      <div class="settings-action-bar" *ngIf="!loading && isStaffRole">
        <button
          type="button"
          mat-stroked-button
          (click)="onPageCancel()"
          [disabled]="savingPageChanges">
          <mat-icon>{{ hasUnsavedChanges ? 'cancel' : 'close' }}</mat-icon>
          {{ hasUnsavedChanges ? 'Cancel' : 'Close' }}
        </button>
        <button
          type="button"
          mat-raised-button
          color="primary"
          (click)="onPageSave()"
          [disabled]="!hasUnsavedChanges || savingPageChanges">
          <mat-icon *ngIf="!savingPageChanges">save</mat-icon>
          <mat-icon *ngIf="savingPageChanges" class="spinning">refresh</mat-icon>
          {{ savingPageChanges ? 'Saving...' : 'Save Changes' }}
        </button>
      </div>
    </mat-card>
  </div>
</div>

@if (showPhoneCollectModal) {
  <div class="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="phone-collect-modal-title">
    <div class="modal-content phone-collect-modal" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <h2 id="phone-collect-modal-title" class="modal-title">Add Your Phone Number</h2>
        <p class="modal-subtitle">
          Enter your mobile number to enable text notifications. We'll send a verification code next.
        </p>
      </div>

      <form class="modal-body" [formGroup]="phoneCollectForm" (ngSubmit)="submitPhoneCollect()">
        <mat-form-field appearance="outline" class="phone-collect-field">
          <mat-label>Mobile Phone Number</mat-label>
          <input
            #phoneCollectInput
            matInput
            type="tel"
            formControlName="phonenumber"
            autocomplete="tel">
          @if (phoneCollectControl?.invalid && phoneCollectControl?.touched) {
            <mat-error>
              @if (phoneCollectControl?.errors?.['required']) {
                Phone number is required
              } @else if (phoneCollectControl?.errors?.['pattern']) {
                Please enter a valid phone number
              }
            </mat-error>
          }
        </mat-form-field>

        @if (phoneCollectError) {
          <div class="phone-otp-error">
            <mat-icon>error</mat-icon>
            <span>{{ phoneCollectError }}</span>
          </div>
        }

        <div class="modal-actions phone-collect-actions">
          <button type="button" mat-stroked-button (click)="cancelPhoneCollectModal()" [disabled]="savingPhone">
            Cancel
          </button>
          <button type="submit" mat-raised-button color="primary" [disabled]="phoneCollectForm.invalid || savingPhone">
            @if (savingPhone) {
              <mat-spinner diameter="20" class="verify-spinner"></mat-spinner>
              Saving...
            } @else {
              Continue
            }
          </button>
        </div>
      </form>
    </div>
  </div>
}

@if (showPhoneOtpModal) {
  <div class="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="phone-otp-modal-title">
    <div class="modal-content phone-otp-modal" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <h2 id="phone-otp-modal-title" class="modal-title">Verify Your Phone Number</h2>
        <p class="modal-subtitle">We've sent a 6-digit verification code to</p>
        <div class="verification-details">
          <div class="phone-display">
            <mat-icon>sms</mat-icon>
            <span>{{ maskedPhone }}</span>
          </div>
          <p class="verification-requirement-note">
            To enable or receive text notifications, please complete this mobile verification.
          </p>
        </div>
      </div>

      <div class="modal-body">
        <div class="phone-otp-input-section">
          <mat-form-field appearance="outline" class="phone-otp-field">
            <mat-label>Enter 6-digit code</mat-label>
            <input
              #phoneOtpInput
              matInput
              type="text"
              inputmode="numeric"
              maxlength="6"
              [(ngModel)]="phoneOtpCode"
              [ngModelOptions]="{ standalone: true }"
              (input)="onPhoneOtpInput($event)"
              autocomplete="one-time-code">
          </mat-form-field>
        </div>

        @if (phoneOtpError) {
          <div class="phone-otp-error">
            <mat-icon>error</mat-icon>
            <span>{{ phoneOtpError }}</span>
          </div>
        }

        <div class="modal-actions">
          <button
            type="button"
            mat-raised-button
            color="primary"
            class="verify-otp-btn"
            [disabled]="phoneOtpCode.length !== 6 || isVerifyingOtp"
            (click)="verifyPhoneOtpInModal()">
            @if (isVerifyingOtp) {
              <mat-spinner diameter="20" class="verify-spinner"></mat-spinner>
              Verifying...
            } @else {
              Verify Code
            }
          </button>
        </div>

        <div class="resend-section">
          <p>Didn't receive the code?</p>
          @if (canResendPhoneOtp) {
            <button
              type="button"
              mat-stroked-button
              class="resend-button"
              (click)="resendPhoneOtpInModal()">
              <mat-icon>refresh</mat-icon>
              Resend Code
            </button>
          } @else {
            <div class="countdown">
              <mat-icon>schedule</mat-icon>
              <span>Resend available in {{ phoneOtpResendCountdown }}s</span>
            </div>
          }
        </div>
      </div>
    </div>
  </div>
}
